/**
 * Rally auto-gift reconciliation (multi-gift) — v2 "één kapitein"
 *
 * Doel: zodra een trigger-product (bril, paddle of tas) in de cart zit, horen
 * de bijbehorende gratis cadeaus erbij. Een Shopify automatic discount maakt
 * die cadeau-regels $0. Verdwijnt het trigger-product, dan verdwijnen de
 * cadeaus. Cadeaus schalen mee met de hoeveelheid trigger-items.
 *
 * Config: window.RLY_AUTO_GIFT_CONFIG = {
 *   rules: [{ variantIds:[id] OF prefixes:[handle-prefix], gifts:[{variantId, qty}], optOutAttr? }],
 *   managedAddons: [{ variantId, maxQty }]   // bv. warranty: max 1, weg zonder betaald item
 * }
 *
 * Architectuur-principes van deze versie (juli 2026 rebuild):
 * 1. HET ANTWOORD IS DE WAARHEID. /cart/update.js geeft de volledige cart én
 *    (via `sections`) de nieuw gerenderde drawer-HTML terug. We dispatchen
 *    Horizon's CartUpdateEvent met precies die data. GEEN extra /cart.js
 *    fetches achteraf, GEEN geforceerde sectionRenderer.renderSection,
 *    GEEN verify-reconcile op een timer, GEEN los cleanup-script.
 * 2. ÉÉN SCHRIJFPAD. Alle correcties (cadeau-aantallen, warranty-cap,
 *    orphan-opruiming) gaan in één update.js-call; alleen ontbrekende
 *    cadeau-regels gaan vooraf via één add.js-call (properties: _gift).
 * 3. DE V2-PDP REGELT ZIJN EIGEN ADD. Knoppen met [data-rvx-owned] slaan we
 *    hier volledig over: de PDP stuurt bril + power bank + warranty al in één
 *    call mee en dispatcht zelf het CartUpdateEvent. Reconcile draait daarna
 *    alleen als vangnet (debounced) en vindt vrijwel altijd "in sync".
 * 4. Gift-regels worden in cart-products.liquid als VERBORGEN rijen
 *    meegerenderd, zodat Shopify's regelnummers altijd kloppen met de DOM
 *    (dat was de bron van "Quantity input not found").
 */
import { CartUpdateEvent } from '@theme/events';

(function () {
  'use strict';

  var CONFIG = window.RLY_AUTO_GIFT_CONFIG;
  if (!CONFIG || !Array.isArray(CONFIG.rules) || !CONFIG.rules.length) {
    console.warn('[rly-auto-gift] config missing or invalid');
    return;
  }

  var RULES = CONFIG.rules;
  var ADDONS = Array.isArray(CONFIG.managedAddons) ? CONFIG.managedAddons : [];
  var DEBUG = !!CONFIG.debug;
  var SOURCE = 'rly-auto-gift';
  var V2_SOURCE = 'rly-vnext-atc';

  // Set van alle gift-variant-ids (als string) over alle regels heen.
  var GIFT_VARIANT_IDS = {};
  RULES.forEach(function (rule) {
    (rule.gifts || []).forEach(function (g) {
      GIFT_VARIANT_IDS[String(g.variantId)] = true;
    });
  });

  var ADDON_IDS = {};
  ADDONS.forEach(function (a) { ADDON_IDS[String(a.variantId)] = a; });

  // Trigger-variant-ids + handle-prefixes: bepalen of een toegevoegd product
  // überhaupt cadeaus kan triggeren.
  var TRIGGER_VARIANT_IDS = {};
  var TRIGGER_PREFIXES = [];
  RULES.forEach(function (rule) {
    (rule.variantIds || []).forEach(function (v) { TRIGGER_VARIANT_IDS[String(v)] = true; });
    (rule.prefixes || []).forEach(function (p) { TRIGGER_PREFIXES.push(p); });
  });
  var lastAddVariantId = null;

  function addIsGiftRelevant() {
    if (!lastAddVariantId) return true;
    if (TRIGGER_VARIANT_IDS[lastAddVariantId]) return true;
    var path = (typeof location !== 'undefined' && location.pathname) || '';
    var m = path.match(/\/products\/([^\/?#]+)/);
    if (!m) return TRIGGER_PREFIXES.length > 0;
    var handle = m[1];
    for (var i = 0; i < TRIGGER_PREFIXES.length; i++) {
      if (handle.indexOf(TRIGGER_PREFIXES[i]) === 0) return true;
    }
    return false;
  }

  var isReconciling = false;
  var reconcileScheduled = false;
  var lastErrorAt = 0;
  var consecutiveErrors = 0;
  var pendingOpenAfterAdd = false;
  var ERROR_COOLDOWN_MS = 5000;
  var MAX_CONSECUTIVE_ERRORS = 3;

  function log() {
    if (!DEBUG) return;
    console.log.apply(console, ['[rly-auto-gift]'].concat(Array.prototype.slice.call(arguments)));
  }

  // ---------- Drawer helpers ----------
  function disableHorizonAutoOpen() {
    document.querySelectorAll('cart-drawer-component[auto-open]').forEach(function (d) {
      d.removeAttribute('auto-open');
    });
  }

  function openCartDrawer() {
    var cd = document.querySelector('cart-drawer-component');
    if (!cd) return;
    if (typeof cd.open === 'function') {
      cd.open();
    } else if (typeof cd.showDialog === 'function') {
      cd.showDialog();
    }
  }

  // ---------- Skeleton loader ----------
  var skeletonSafetyTimer = null;

  function showSkeleton() {
    document.body.classList.add('rly-cart-skeleton-active');
    var sk = document.querySelector('.rly-cart-skeleton');
    if (sk) sk.removeAttribute('hidden');
    if (skeletonSafetyTimer) clearTimeout(skeletonSafetyTimer);
    skeletonSafetyTimer = setTimeout(function () {
      log('skeleton safety timeout fired (5s) — force hiding');
      hideSkeleton();
    }, 5000);
  }

  function hideSkeleton() {
    document.body.classList.remove('rly-cart-skeleton-active');
    if (skeletonSafetyTimer) {
      clearTimeout(skeletonSafetyTimer);
      skeletonSafetyTimer = null;
    }
  }

  // ---------- Rule matching / targets ----------
  function matchRuleForItem(item) {
    if (!item) return null;
    var vid = String(item.variant_id);
    var handle = String(item.handle || item.product_handle || '');
    for (var i = 0; i < RULES.length; i++) {
      var rule = RULES[i];
      var vids = rule.variantIds || [];
      for (var k = 0; k < vids.length; k++) {
        if (String(vids[k]) === vid) return rule;
      }
      var prefixes = rule.prefixes || [];
      for (var j = 0; j < prefixes.length; j++) {
        if (handle && handle.indexOf(prefixes[j]) === 0) return rule;
      }
    }
    return null;
  }

  function isManagedGift(item) {
    return !!(item && GIFT_VARIANT_IDS[String(item.variant_id)]);
  }

  function isManagedAddon(item) {
    return !!(item && ADDON_IDS[String(item.variant_id)]);
  }

  /**
   * Doel-aantal per gift-variant: som over alle (niet-gift) trigger-items van
   * item.quantity × gift.qty. Respecteert optOutAttr per regel.
   */
  function computeTargets(items, attributes) {
    var targets = {};
    Object.keys(GIFT_VARIANT_IDS).forEach(function (v) { targets[v] = 0; });
    if (!items) return targets;
    attributes = attributes || {};
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (isManagedGift(item)) continue;
      var rule = matchRuleForItem(item);
      if (!rule) continue;
      if (rule.optOutAttr && attributes[rule.optOutAttr]) continue;
      var triggerQty = item.quantity || 0;
      (rule.gifts || []).forEach(function (g) {
        var key = String(g.variantId);
        targets[key] = (targets[key] || 0) + triggerQty * (g.qty || 1);
      });
    }
    return targets;
  }

  /**
   * Beheerde betaalde add-ons (bv. warranty). Spelregels (2026-07-13, per bril):
   * - De toggle-stand leeft als cart-attribute (optInAttr), net als de
   *   power bank opt-out. PDP-toggle en cart-blok zetten dezelfde attribute.
   * - Staat de toggle AAN: doel-aantal = som van de trigger-items
   *   (scaleWithVariantIds), dus 2 brillen = 2 warranties. De motor voegt
   *   toe, schaalt mee (op én af) en ruimt op — zelfde principe als cadeaus.
   * - Staat de toggle UIT of zijn er geen triggers meer: doel = 0.
   * @returns {Object} { variantIdString: gewenstAantal } voor ALLE geconfigureerde addons.
   */
  function computeAddonTargets(items, attributes) {
    var targets = {};
    if (!items) items = [];
    attributes = attributes || {};
    ADDONS.forEach(function (cfg) {
      var key = String(cfg.variantId);
      var on = cfg.optInAttr ? !!attributes[cfg.optInAttr] : true;
      if (!on) { targets[key] = 0; return; }
      var scaleIds = (cfg.scaleWithVariantIds || []).map(String);
      var triggerQty = 0;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (isManagedGift(it) || isManagedAddon(it)) continue;
        if (scaleIds.indexOf(String(it.variant_id)) !== -1) triggerQty += it.quantity || 0;
      }
      var want = triggerQty;
      if (cfg.maxQty) want = Math.min(want, cfg.maxQty);
      targets[key] = want;
    });
    return targets;
  }

  /**
   * Som van line-items EXCLUSIEF gift-items, voor de cart-icon bubble.
   */
  function countVisibleItems(cart) {
    if (!cart || !cart.items) return 0;
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (!isManagedGift(cart.items[i])) total += cart.items[i].quantity || 0;
    }
    return total;
  }

  function collectSectionIds() {
    var ids = [];
    var cartItemsNodes = document.querySelectorAll('cart-items-component[data-section-id]');
    for (var i = 0; i < cartItemsNodes.length; i++) {
      var sid = cartItemsNodes[i].getAttribute('data-section-id');
      if (sid && ids.indexOf(sid) === -1) ids.push(sid);
    }
    var icons = document.querySelectorAll('cart-icon[data-section-id]');
    for (var j = 0; j < icons.length; j++) {
      var iid = icons[j].getAttribute('data-section-id');
      if (iid && ids.indexOf(iid) === -1) ids.push(iid);
    }
    return ids;
  }

  // ---------- Cart API ----------
  // Laatst bekende cart-staat (uit elke fetch/update). Gebruikt door de
  // teller-overname om te weten of de cart beheerde items bevat.
  var lastCart = null;

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch ' + r.status);
      return r.json();
    }).then(function (cart) {
      lastCart = cart;
      return cart;
    });
  }

  /**
   * Voeg ontbrekende regels toe in 1 call (zonder sections — de afsluitende
   * update.js levert de verse render). Cadeaus krijgen de _gift property;
   * betaalde add-ons (warranty) zijn gewone regels zonder property.
   */
  function addItems(adds) {
    var payload = {
      items: adds.map(function (a) {
        var item = { id: Number(a.id), quantity: Math.max(1, Number(a.quantity) || 1) };
        if (a.gift) item.properties = { _gift: 'true' };
        return item;
      })
    };
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) return r.text().then(function (t) { throw new Error('add items ' + r.status + ' ' + t); });
      return r.json();
    });
  }

  /**
   * De afsluitende call van elke reconcile-ronde: qty-correcties (en evt.
   * attributes) in één atomaire update.js MET sections. Het antwoord bevat de
   * volledige cart + verse drawer-HTML en is daarmee de enige waarheid.
   */
  function updateCart(updates, attributes) {
    var payload = {};
    if (updates && Object.keys(updates).length) payload.updates = updates;
    if (attributes) payload.attributes = attributes;
    var sectionIds = collectSectionIds();
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) return r.text().then(function (t) { throw new Error('update cart ' + r.status + ' ' + t); });
      return r.json();
    }).then(function (cart) {
      lastCart = cart;
      return cart;
    });
  }

  function dispatchHorizonCartUpdate(cart, sections) {
    var ev = new CartUpdateEvent(cart, SOURCE, {
      source: SOURCE,
      sections: sections || {},
      itemCount: countVisibleItems(cart)
    });
    document.dispatchEvent(ev);
  }

  // ---------- Planning ----------
  /**
   * Bouwt het complete correctie-plan voor een (gesimuleerde) cart-staat.
   *
   * BELANGRIJK: Shopify's discount-engine kan regels SPLITSEN zodra het
   * kortings-tegoed niet alle stuks dekt (power bank "1 gratis + 2 betaald",
   * of de bril zelf als "2 + 1"). Daarom telt dit plan per variant de SOM
   * over alle regels en corrigeert het via LINE KEYS (uniek per regel) in
   * plaats van variant-ids. Identieke betaalde regels worden samengevoegd.
   */
  function buildPlan(items, attributes) {
    var targets = computeTargets(items, attributes);
    var addonTargets = computeAddonTargets(items, attributes);

    var giftLines = {};
    var addonLines = {};
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var vk = String(it.variant_id);
      if (isManagedGift(it)) {
        (giftLines[vk] = giftLines[vk] || []).push({ key: it.key, qty: it.quantity || 0 });
      } else if (isManagedAddon(it)) {
        (addonLines[vk] = addonLines[vk] || []).push({ key: it.key, qty: it.quantity || 0 });
      }
    }

    var plan = { adds: [], updates: {}, changed: false, targets: targets, addonTargets: addonTargets };

    // Per variant: eerste regel krijgt het doel-aantal, alle overige
    // (discount-splits) gaan naar 0 zodat alles weer één regel wordt.
    function planVariant(lines, want, variantId, isGiftLine) {
      if (!lines || !lines.length) {
        if (want > 0) { plan.adds.push({ id: Number(variantId), quantity: want, gift: isGiftLine }); plan.changed = true; }
        return;
      }
      if (lines[0].qty !== want) { plan.updates[lines[0].key] = want; plan.changed = true; }
      for (var k = 1; k < lines.length; k++) {
        if (lines[k].qty !== 0) { plan.updates[lines[k].key] = 0; plan.changed = true; }
      }
    }

    Object.keys(GIFT_VARIANT_IDS).forEach(function (v) {
      planVariant(giftLines[v], targets[v] || 0, v, true);
    });
    Object.keys(addonTargets).forEach(function (v) {
      planVariant(addonLines[v], addonTargets[v] || 0, v, false);
    });

    // Gesplitste BETAALDE regels (zelfde variant + zelfde properties) samenvoegen.
    var dupGroups = {};
    for (var d = 0; d < items.length; d++) {
      var di = items[d];
      if (isManagedGift(di) || isManagedAddon(di)) continue; // hierboven al gepland
      var sig = String(di.variant_id) + '|' + JSON.stringify(di.properties || {});
      (dupGroups[sig] = dupGroups[sig] || []).push({ key: di.key, qty: di.quantity || 0 });
    }
    Object.keys(dupGroups).forEach(function (sig) {
      var dl = dupGroups[sig];
      if (dl.length < 2) return;
      var sum = 0;
      for (var s = 0; s < dl.length; s++) sum += dl[s].qty;
      if (dl[0].qty !== sum) { plan.updates[dl[0].key] = sum; plan.changed = true; }
      for (var s2 = 1; s2 < dl.length; s2++) {
        if (dl[s2].qty !== 0) { plan.updates[dl[s2].key] = 0; plan.changed = true; }
      }
    });

    return plan;
  }

  /**
   * Voert een plan uit: eerst ontbrekende regels toevoegen (zonder sections),
   * dan altijd één afsluitende update.js MET sections. Het antwoord (volledige
   * cart + verse drawer-HTML) wordt als CartUpdateEvent gedispatcht: één
   * tekenmoment voor Horizon.
   */
  function executePlan(plan) {
    var chain = Promise.resolve();
    if (plan.adds.length) {
      chain = chain.then(function () { return addItems(plan.adds); });
    }
    return chain
      .then(function () { return updateCart(plan.updates, null); })
      .then(function (resp) {
        dispatchHorizonCartUpdate(resp, resp.sections || {});
      });
  }

  // ---------- Reconcile ----------
  /**
   * Brengt de cart naar de doel-staat. Schrijft alléén als er iets afwijkt.
   * Geen wijzigingen nodig? Dan ook geen calls en geen re-render.
   */
  function reconcile() {
    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
      log('circuit breaker open, stopping');
      finishRound();
      return Promise.resolve();
    }
    if (lastErrorAt && (Date.now() - lastErrorAt) < ERROR_COOLDOWN_MS) {
      log('skip reconcile (error cooldown)');
      finishRound();
      return Promise.resolve();
    }
    if (isReconciling) {
      reconcileScheduled = true;
      return Promise.resolve();
    }

    isReconciling = true;
    log('reconcile start');
    document.body.classList.add('rly-cart-syncing');

    return fetchCart()
      .then(function (cart) {
        var plan = buildPlan(cart.items, cart.attributes);
        log('state', plan);
        if (!plan.changed) {
          log('action: none (in sync)');
          return null; // geen calls, geen event — Horizon's eigen render is al correct
        }
        return executePlan(plan);
      })
      .then(function () {
        consecutiveErrors = 0;
      })
      .catch(function (err) {
        console.error('[rly-auto-gift]', err);
        lastErrorAt = Date.now();
        consecutiveErrors++;
      })
      .then(function () {
        isReconciling = false;
        if (!hasPendingQty()) document.body.classList.remove('rly-cart-syncing');
        finishRound();
        if (reconcileScheduled) {
          reconcileScheduled = false;
          return reconcile();
        }
      });
  }

  // Sluit een ronde af: skeleton weg + (indien gevraagd) drawer open.
  // De drawer-inhoud is op dit punt al gemorpht door component-cart-items
  // (via het CartUpdateEvent met sections), of was al correct (in sync).
  function finishRound() {
    if (pendingCheckout && !document.body.classList.contains('rly-cart-syncing')) {
      goToCheckout();
      return;
    }
    if (pendingOpenAfterAdd) {
      pendingOpenAfterAdd = false;
      hideSkeleton();
      openCartDrawer();
    } else if (document.body.classList.contains('rly-cart-skeleton-active')) {
      hideSkeleton();
    }
  }

  var debouncedReconcile = (function () {
    var t = null;
    return function () {
      if (t) clearTimeout(t);
      // Korte debounce: qty-kliks bundelen zonder merkbare vertraging.
      t = setTimeout(function () { t = null; reconcile(); }, 350);
    };
  })();

  // ---------- Teller-overname: één tekenmoment voor qty-wijzigingen ----------
  // Horizon's eigen flow is: /cart/change -> render (mét Shopify's tijdelijke
  // discount-splitsing als dubbele regel zichtbaar) -> onze correctie ->
  // tweede render. Door de teller-klik hier in de capture-fase af te vangen
  // bouwen we de complete eindstaat (bril + cadeaus + warranty + samengevoegde
  // splits) en schrijven die in ÉÉN keer: de tussenstand bestaat nooit op het
  // scherm. Alleen actief wanneer de cart beheerde items bevat — anders laten
  // we Horizon's native flow met rust.
  var pendingQty = {};   // cartLine (1-based, Shopify-regelnummer) -> gewenste qty
  var takeoverTimer = null;

  function hasPendingQty() {
    return Object.keys(pendingQty).length > 0;
  }

  function cartIsManaged(cart) {
    if (!cart || !cart.items) return false;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isManagedGift(it) || isManagedAddon(it) || matchRuleForItem(it)) return true;
    }
    return false;
  }

  document.addEventListener('quantity-selector:update', function (e) {
    var d = e && e.detail;
    if (!d || !d.cartLine) return;                      // PDP-selectors hebben geen cartLine
    if (!lastCart || !cartIsManaged(lastCart)) return;  // niet-beheerde cart: native flow
    e.stopImmediatePropagation();                       // blokkeer Horizon's /cart/change-pad
    pendingQty[String(d.cartLine)] = Number(d.quantity);
    document.body.classList.add('rly-cart-syncing');    // dim + checkout-slot per direct
    if (takeoverTimer) clearTimeout(takeoverTimer);
    takeoverTimer = setTimeout(function () { takeoverTimer = null; applyQtyTakeover(); }, 300);
  }, true);

  function applyQtyTakeover() {
    if (!hasPendingQty()) return;
    if (isReconciling) { setTimeout(applyQtyTakeover, 150); return; }
    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
      // Stroomonderbreker open: niet blijven hangen in gedimde staat.
      pendingQty = {};
      document.body.classList.remove('rly-cart-syncing');
      return;
    }
    isReconciling = true;
    document.body.classList.add('rly-cart-syncing');

    fetchCart()
      .then(function (cart) {
        var changes = pendingQty;
        pendingQty = {};

        // Simuleer de eindstaat: teller-wijzigingen toegepast op de verse cart.
        var simulated = [];
        var indexOk = true;
        for (var i = 0; i < cart.items.length; i++) {
          var src = cart.items[i];
          simulated.push({ variant_id: src.variant_id, key: src.key, quantity: src.quantity || 0, properties: src.properties || null });
        }
        Object.keys(changes).forEach(function (lineStr) {
          var idx = Number(lineStr) - 1;
          if (!simulated[idx]) { indexOk = false; return; }
          simulated[idx].quantity = Math.max(0, Number(changes[lineStr]) || 0);
        });

        var plan;
        if (indexOk) {
          plan = buildPlan(simulated, cart.attributes);
          // De teller-wijziging zelf meesturen wanneer het plan die regel nog
          // niet raakt (bv. bril omhoog terwijl er niets samen te voegen valt).
          Object.keys(changes).forEach(function (lineStr) {
            var idx2 = Number(lineStr) - 1;
            var live = cart.items[idx2];
            if (!live) return;
            var wantQty = simulated[idx2].quantity;
            if (plan.updates[live.key] === undefined && (live.quantity || 0) !== wantQty) {
              plan.updates[live.key] = wantQty;
              plan.changed = true;
            }
          });
        } else {
          // Regel-index klopt niet meer met de live cart (zeldzaam, bv. na een
          // gelijktijdige wijziging): val terug op een gewone reconcile van de
          // werkelijke staat — self-healing, nooit een verkeerde regel raken.
          log('takeover: regel-index verlopen, terugval op reconcile');
          plan = buildPlan(cart.items, cart.attributes);
        }

        log('takeover', { changes: changes, updates: plan.updates, adds: plan.adds });
        if (!plan.changed) return null;
        return executePlan(plan);
      })
      .then(function () {
        consecutiveErrors = 0;
      })
      .catch(function (err) {
        console.error('[rly-auto-gift] takeover', err);
        lastErrorAt = Date.now();
        consecutiveErrors++;
      })
      .then(function () {
        isReconciling = false;
        if (hasPendingQty()) { applyQtyTakeover(); return; }
        document.body.classList.remove('rly-cart-syncing');
        finishRound();
        if (reconcileScheduled) {
          reconcileScheduled = false;
          reconcile();
        }
      });
  }

  // ---------- Checkout-regisseur (2026-07-17) ----------
  // Eén handler BEZIT de checkout-klik. Spelregels (invariants):
  //   1. een checkout-klik gaat nooit verloren;
  //   2. er wordt nooit genavigeerd terwijl een cart-call nog onderweg is;
  //   3. we navigeren DIRECT naar /checkout (geen form-POST-omweg, scheelt
  //      ~0,6s — zelfde aanpak als de gemeten top-converting stores).
  // Gedrag: cart synct nog -> klik in wachtrij ("Updating cart…"), na de
  // sync automatisch door (hook in finishRound); cart idle -> meteen gaan.
  // Vangnet: een wachtrij-klik navigeert na 4s hoe dan ook (de cart is
  // server-side altijd geldig). Uitzetten kan met directCheckout:false in
  // de config (dan geldt alleen het oude blokkeer-gedrag).
  var DIRECT_CHECKOUT = CONFIG.directCheckout !== false;
  var pendingCheckout = false;
  var checkoutNavigating = false;
  var pendingCheckoutTimer = null;

  function setCheckoutLabels(txt) {
    var btns = document.querySelectorAll('button[name="checkout"]');
    for (var i = 0; i < btns.length; i++) {
      if (!btns[i].dataset.rlyCoLabel) btns[i].dataset.rlyCoLabel = btns[i].textContent;
      btns[i].textContent = txt;
    }
  }
  function restoreCheckoutLabels() {
    var btns = document.querySelectorAll('button[name="checkout"]');
    for (var i = 0; i < btns.length; i++) {
      if (btns[i].dataset.rlyCoLabel) {
        btns[i].textContent = btns[i].dataset.rlyCoLabel;
        btns[i].dataset.rlyCoLabel = '';
      }
    }
  }
  function goToCheckout() {
    if (checkoutNavigating) return;
    checkoutNavigating = true;
    pendingCheckout = false;
    if (pendingCheckoutTimer) { clearTimeout(pendingCheckoutTimer); pendingCheckoutTimer = null; }
    setCheckoutLabels('Preparing secure checkout…');
    document.body.classList.add('rly-checkout-navigating');
    log('naar checkout (direct)');
    window.location.href = '/checkout';
  }
  // Terug-knop vanaf de checkout (bfcache): alles netjes resetten.
  if (typeof window.addEventListener === 'function') {
    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted) {
        checkoutNavigating = false;
        pendingCheckout = false;
        if (pendingCheckoutTimer) { clearTimeout(pendingCheckoutTimer); pendingCheckoutTimer = null; }
        document.body.classList.remove('rly-checkout-navigating');
        restoreCheckoutLabels();
      }
    });
  }
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    var coBtn = e.target.closest('button[name="checkout"]');
    var syncing = document.body.classList.contains('rly-cart-syncing');
    if (!coBtn) {
      // Overige summary-elementen: alleen blokkeren tijdens sync (oud gedrag).
      var ander = e.target.closest('.cart-drawer__summary button, .cart-drawer__summary a');
      if (ander && syncing) { e.preventDefault(); e.stopImmediatePropagation(); }
      return;
    }
    if (!syncing && !DIRECT_CHECKOUT) return; // vlag uit: klassieke form-POST
    e.preventDefault();
    e.stopImmediatePropagation();
    if (checkoutNavigating) return; // dubbelklik: al onderweg
    if (syncing) {
      if (pendingCheckout) return;
      pendingCheckout = true;
      setCheckoutLabels('Updating cart…');
      log('checkout in wachtrij: cart is nog aan het bijwerken');
      pendingCheckoutTimer = setTimeout(function () {
        if (pendingCheckout && !checkoutNavigating) {
          log('wachtrij-vangnet (4s): alsnog naar checkout');
          goToCheckout();
        }
      }, 4000);
    } else {
      goToCheckout();
    }
  }, true);
  document.addEventListener('submit', function (e) {
    if (!document.body.classList.contains('rly-cart-syncing')) return;
    var t = e.target;
    if (t && t.closest && t.closest('cart-drawer-component, cart-items-component')) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);

  // ---------- ATC button "in flight" state (anti dubbel-add + feedback) ----------
  function setAddingState(btn) {
    btn.dataset.rlyAdding = '1';
    var labelEl = btn.querySelector('.rly-pdp__atc-label') || btn;
    if (!btn.dataset.rlyLabel) btn.dataset.rlyLabel = labelEl.textContent;
    labelEl.textContent = 'Adding…';
    btn.style.opacity = '0.75';
    btn.style.cursor = 'wait';
  }
  function clearAddingState() {
    var nodes = document.querySelectorAll('button[name="add"][data-rly-adding="1"]');
    for (var i = 0; i < nodes.length; i++) {
      var btn = nodes[i];
      btn.dataset.rlyAdding = '';
      var labelEl = btn.querySelector('.rly-pdp__atc-label') || btn;
      if (btn.dataset.rlyLabel) { labelEl.textContent = btn.dataset.rlyLabel; btn.dataset.rlyLabel = ''; }
      btn.style.opacity = '';
      btn.style.cursor = '';
    }
  }

  // ---------- Pre-flight ATC intercept ----------
  // Geldt NIET voor knoppen die hun add zelf volledig regelen ([data-rvx-owned],
  // zoals de Rally Vision v2 PDP): die sturen alles in één call en dispatchen
  // zelf het CartUpdateEvent.
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    var btn = e.target.closest('button[name="add"][type="submit"]');
    if (!btn) return;
    if (btn.hasAttribute('data-rvx-owned')) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    if (btn.dataset.rlyAdding === '1') {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    var _form = btn.closest('form');
    var _idInput = _form ? _form.querySelector('[name="id"]') : null;
    lastAddVariantId = _idInput ? String(_idInput.value) : null;
    log('ATC button click intercepted — pre-flight skeleton + drawer');
    setAddingState(btn);
    pendingOpenAfterAdd = true;
    showSkeleton();
    openCartDrawer();
    setTimeout(clearAddingState, 6000);
  }, true);

  // ---------- Power bank "No thanks" in de cart-drawer ----------
  // Zelfde schakelaar als de PDP-toggle: zet de opt-out attribute EN haalt de
  // power bank weg, in één update.js-call. De PDP-toggle synct mee via het
  // CartUpdateEvent (de v2-sectie luistert daarop).
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    var el = e.target.closest('[data-rly-pb-optout]');
    if (!el) return;
    e.preventDefault();
    // Scoping (2026-07-17): met meerdere cadeau-regels mag "No thanks" op het
    // ene kaartje niet het andere cadeau raken. De knop draagt zijn attribute;
    // zonder data-rly-optout-attr geldt het oude gedrag (alle regels).
    var scopeAttr = el.getAttribute('data-rly-optout-attr');
    var optOutRules = RULES.filter(function (r) { return r.optOutAttr && (!scopeAttr || r.optOutAttr === scopeAttr); });
    if (!optOutRules.length) return;
    var attrs = {};
    optOutRules.forEach(function (r) { attrs[r.optOutAttr] = 'true'; });
    el.setAttribute('disabled', 'disabled');
    fetchCart()
      .then(function (cart) {
        // Verwijderen via LINE KEYS: bij een discount-split bestaan er meerdere
        // regels van dezelfde variant — een variant-id-update raakt er maar één.
        var updates = {};
        optOutRules.forEach(function (r) {
          (r.gifts || []).forEach(function (g) {
            for (var i = 0; i < cart.items.length; i++) {
              var it = cart.items[i];
              if (String(it.variant_id) === String(g.variantId) && isManagedGift(it) && (it.quantity || 0) > 0) {
                updates[it.key] = 0;
              }
            }
          });
        });
        return updateCart(updates, attrs);
      })
      .then(function (resp) {
        dispatchHorizonCartUpdate(resp, resp.sections || {});
      })
      .catch(function (err) {
        console.error('[rly-auto-gift] pb opt-out', err);
        el.removeAttribute('disabled');
      });
  });

  // ---------- Warranty-toggle (cart-blok boven Subtotal) ----------
  // Zelfde schakelaar als de PDP-toggle: zet de opt-in attribute; de reconcile
  // voegt de warranty toe / schaalt / verwijdert (per bril). De v2-PDP synct
  // zijn eigen toggle via het CartUpdateEvent.
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    var el = e.target.closest('[data-rly-warr-toggle]');
    if (!el || el.hasAttribute('disabled')) return;
    e.preventDefault();
    var addonRules = ADDONS.filter(function (a) { return a.optInAttr; });
    if (!addonRules.length) return;
    var turnOn = el.getAttribute('aria-pressed') !== 'true';
    var attrs = {};
    addonRules.forEach(function (a) { attrs[a.optInAttr] = turnOn ? 'true' : ''; });
    el.setAttribute('disabled', 'disabled');
    el.setAttribute('aria-pressed', turnOn ? 'true' : 'false');
    el.classList.toggle('is-on', turnOn);
    updateCart(null, attrs)
      .then(function (resp) {
        dispatchHorizonCartUpdate(resp, resp.sections || {});
        // Direct reconcilen (geen debounce): de klant wacht op dit resultaat.
        return reconcile();
      })
      .catch(function (err) {
        console.error('[rly-auto-gift] warranty toggle', err);
      })
      .then(function () {
        el.removeAttribute('disabled');
      });
  });

  document.addEventListener('cart:error', function (e) {
    log('cart:error received — hiding skeleton', e && e.detail);
    pendingOpenAfterAdd = false;
    clearAddingState();
    hideSkeleton();
  });

  document.addEventListener('cart:update', function (e) {
    var src = null;
    var didError = false;
    if (e && e.detail) {
      // Volledige cart in het event (bv. v2 one-call add)? Cache verversen.
      if (e.detail.resource && e.detail.resource.items) lastCart = e.detail.resource;
      src = (e.detail.data && e.detail.data.source) || e.detail.source;
      didError = !!(e.detail.didError || (e.detail.data && e.detail.data.didError));
      if (src === SOURCE) {
        log('skip own event');
        return;
      }
    }

    if (didError) {
      log('cart:update with didError — hiding skeleton');
      pendingOpenAfterAdd = false;
      clearAddingState();
      hideSkeleton();
      return;
    }

    // V2-PDP heeft alles al in één call gedaan (incl. cadeau + warranty).
    // Alleen een stil vangnet draaien; geen skeleton/drawer-regie van ons.
    if (src === V2_SOURCE) {
      log('v2 one-call add — silent safety reconcile');
      debouncedReconcile();
      return;
    }

    var isAddEvent = src === 'product-form-component' || src === 'quick-add-component';
    if (isAddEvent) {
      clearAddingState();
      if (addIsGiftRelevant()) {
        pendingOpenAfterAdd = true;
        showSkeleton();
        openCartDrawer();
        reconcile();
      } else {
        log('gift-irrelevante add — Horizon render is al compleet');
        pendingOpenAfterAdd = false;
        hideSkeleton();
        openCartDrawer();
      }
    } else {
      log('cart update from', src, '— debounced reconcile');
      debouncedReconcile();
    }
  });

  // ---------- Initial run ----------
  function initialRun() {
    fetchCart().then(function (cart) {
      var targets = computeTargets(cart.items, cart.attributes);
      var addonTargets = computeAddonTargets(cart.items, cart.attributes);
      var mismatch = false;
      var haveByVariant = {};
      cart.items.forEach(function (it) {
        var key = String(it.variant_id);
        haveByVariant[key] = (haveByVariant[key] || 0) + (it.quantity || 0);
      });
      Object.keys(GIFT_VARIANT_IDS).forEach(function (v) {
        if ((targets[v] || 0) !== (haveByVariant[v] || 0)) mismatch = true;
      });
      Object.keys(addonTargets).forEach(function (v) {
        if ((addonTargets[v] || 0) !== (haveByVariant[v] || 0)) mismatch = true;
      });
      if (mismatch) {
        log('initial run: cart out of sync, reconciling');
        reconcile();
      } else {
        log('initial run: cart in sync, no action');
      }
    }).catch(function (e) {
      log('initial run fetch failed', e);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      disableHorizonAutoOpen();
      initialRun();
    });
  } else {
    disableHorizonAutoOpen();
    initialRun();
  }
})();
